/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * The sagas for Widgets
 *
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @version 1.15.0
 * @since 1.0.1
 */

import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';

import { API_HOSTNAME, WIDGET_META } from '../../../../modules/app/Api';
import {
    onboardingInitStartAction,
    onboardingPageReadyForStartAction,
} from '../../../../modules/onboarding/onboardingActions';
import { ONBOARDING_INIT_SUCCESS } from '../../../../modules/onboarding/onboardingConstants';
import { EOnboardingPage } from '../../../../modules/onboarding/types/onboardingTypes';
import { requestSaga } from '../../../../sagas/requestSaga';
import { LOAD_WIDGET_META } from './constants/widgetActionConstants';
import { loadWidgetMetaFailed, loadWidgetMetaSuccessful } from './widgetActions';
import {
    WIDGET_COSTS_LOAD_CHART,
    WIDGET_COSTS_LOAD_CHART_ERROR,
    WIDGET_COSTS_LOAD_CHART_SUCCESSFUL,
    WIDGET_COSTS_REDIRECT_TO_PAYMENTS,
} from './WidgetCosts/constant/actionConstants';
import { widgetCostsLoadChartSaga, widgetCostsRedirectToPaymentsSaga } from './WidgetCosts/widgetCostsSaga';
import { WIDGET_MULTI_BALANCES_GET_LIST_START } from './WidgetMultiBalances/widgetMultiBalancesConstants';
import { widgetMultiBalancesGetListSaga } from './WidgetMultiBalances/widgetMultiBalancesSaga';
import {
    LOAD_LIST_INQUIRIES_FAIL,
    LOAD_LIST_INQUIRIES_REQUEST,
    LOAD_LIST_INQUIRIES_SUCCESS,
    LOAD_LIST_ORDERS_REQUEST,
} from './WidgetOrderInquiries/widgetOrderInquiriesConstants';
import { loadInquiriesListSaga, loadOrdersListSaga } from './WidgetOrderInquiries/widgetOrderInquiriesSaga';
import {
    LOAD_LIST_PAYMENT_HISTORY_FAIL,
    LOAD_LIST_PAYMENT_HISTORY_REQUEST,
    LOAD_LIST_PAYMENT_HISTORY_SUCCESS,
} from './WidgetPaymentHistory/widgetPaymentHistoryConstants';
import { loadPaymentsHistoryListSaga } from './WidgetPaymentHistory/widgetPaymentHistorySaga';
import { WIDGET_PERSONAL_ACCOUNT_LOAD_DATA } from './WidgetPersonalAccount/constants/widgetPersonalAccountActionConstants';
import { widgetPersonalAccountLoadDataSaga } from './WidgetPersonalAccount/WidgetPersonalAccountSaga';
import { widgetSearchAccountsSagas } from './WidgetSearchAccounts/widgetSearchAccountsSagas';
import { makeSelectorWidgetMetaList } from './widgetSelectors';
import {
    LOAD_WIDGET_SUBSCRIBER_MOBILE_REQUEST,
    WIDGET_SUBSCRIBER_MOBILE_REDIRECT,
} from './WidgetSubscriberMobile/widgetSubscriberMobileConstants';
import {
    widgetSubscriberMobileLoadDataSaga,
    widgetSubscriberMobileRedirectSaga,
} from './WidgetSubscriberMobile/widgetSubscriberMobileSaga';
import { LOAD_LIST_SUBSCRIBERS_MAX_COSTS_REQUEST } from './WidgetSubscribersMaxCosts/constants/widgetSubscriberMaxCostsConstants';
import { loadSubscribersListSaga } from './WidgetSubscribersMaxCosts/widgetSubscriberMaxCostsSaga';
import { EWidgetNames, TWidgetMetaList } from './widgetTypes';
import { WIDGET_VOTING_LOAD_SURVEY } from './WidgetVoting/constants/widgetVotingActionConstants';
import { widgetVotingLoadSurveySaga } from './WidgetVoting/widgetVotingSaga';

export function* loadWidgetMetaSaga(): SagaIterator {
    try {
        const URL = API_HOSTNAME + WIDGET_META;
        const response = yield call(requestSaga, URL, 'GET');
        yield put(loadWidgetMetaSuccessful(response?.metadata || []));

        yield call(waitWidgetLoadForOnboardingInitSaga);
    } catch (e: any) {
        yield put(loadWidgetMetaFailed(e));
    }
}

export function* waitWidgetLoadForOnboardingInitSaga() {
    const meta: TWidgetMetaList = yield select(makeSelectorWidgetMetaList());
    const widgetName: EWidgetNames[] = meta.map(item => item.name);
    const waitForWidgets = [];

    if (widgetName.includes(EWidgetNames.ORDERS_INQUIRIES)) {
        waitForWidgets.push(take([LOAD_LIST_INQUIRIES_SUCCESS, LOAD_LIST_INQUIRIES_FAIL]));
    }
    if (widgetName.includes(EWidgetNames.COSTS)) {
        waitForWidgets.push(take([WIDGET_COSTS_LOAD_CHART_SUCCESSFUL, WIDGET_COSTS_LOAD_CHART_ERROR]));
    }
    if (widgetName.includes(EWidgetNames.PAYMENT_HISTORY)) {
        waitForWidgets.push(take([LOAD_LIST_PAYMENT_HISTORY_SUCCESS, LOAD_LIST_PAYMENT_HISTORY_FAIL]));
    }

    yield all(waitForWidgets);

    yield put(onboardingInitStartAction({ page: EOnboardingPage.dashboard }));
    yield take(ONBOARDING_INIT_SUCCESS);

    yield put(onboardingPageReadyForStartAction());
}

export function* widgetSaga(): SagaIterator {
    yield fork(widgetSearchAccountsSagas);
    yield all([
        takeEvery(LOAD_LIST_INQUIRIES_REQUEST, loadInquiriesListSaga),
        takeEvery(LOAD_LIST_ORDERS_REQUEST, loadOrdersListSaga),
        takeEvery(LOAD_LIST_PAYMENT_HISTORY_REQUEST, loadPaymentsHistoryListSaga),
        takeEvery(LOAD_LIST_SUBSCRIBERS_MAX_COSTS_REQUEST, loadSubscribersListSaga),
        takeEvery(LOAD_WIDGET_META, loadWidgetMetaSaga),
        takeEvery(LOAD_WIDGET_SUBSCRIBER_MOBILE_REQUEST, widgetSubscriberMobileLoadDataSaga),
        takeEvery(WIDGET_COSTS_LOAD_CHART, widgetCostsLoadChartSaga),
        takeEvery(WIDGET_COSTS_REDIRECT_TO_PAYMENTS, widgetCostsRedirectToPaymentsSaga),
        takeEvery(WIDGET_PERSONAL_ACCOUNT_LOAD_DATA, widgetPersonalAccountLoadDataSaga),
        takeEvery(WIDGET_SUBSCRIBER_MOBILE_REDIRECT, widgetSubscriberMobileRedirectSaga),
        takeEvery(WIDGET_VOTING_LOAD_SURVEY, widgetVotingLoadSurveySaga),
        takeEvery(WIDGET_MULTI_BALANCES_GET_LIST_START, widgetMultiBalancesGetListSaga),
    ]);
}
